import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistryEpisodeSummary } from '../../../types';

interface EpisodeSummaryCardProps {
  summary: RegistryEpisodeSummary;
  /** 悬浮面板里「查看原剧本」模式渲染的剧本视图（不传则不显示切换按钮）。 */
  scriptView?: React.ReactNode;
}

const tagStyle: React.CSSProperties = {
  fontSize: '0.75rem', padding: '2px 9px', borderRadius: '999px',
  background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};

export default function EpisodeSummaryCard({ summary, scriptView }: EpisodeSummaryCardProps) {
  const { t } = useTranslation('episodeDetail');
  const cardRef = useRef<HTMLDivElement>(null);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // 面板模式：梗概 / 剧本（查看原剧本）。同一集内记住浏览位置（模式 + 梗概滚动），
  // 关闭重开恢复；切集由父层 key={episode.id} 整体重置。
  const [mode, setMode] = useState<'summary' | 'script'>('summary');
  const summaryWrapRef = useRef<HTMLDivElement>(null);
  const summaryScrollPos = useRef(0);
  useLayoutEffect(() => {
    if (panelOpen && mode === 'summary' && summaryWrapRef.current) {
      summaryWrapRef.current.scrollTop = summaryScrollPos.current;
    }
  }, [panelOpen, mode]);

  // 检测梗概卡是否已滚出视野（越过顶栏）。是 → 显示右上角胶囊。
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 58;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const past = !entry.isIntersecting;
        setScrolledPast(past);
        if (!past) setPanelOpen(false);
      },
      { rootMargin: `-${headerH}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hasChars = (summary.key_characters?.length ?? 0) > 0;
  const hasScenes = (summary.key_scenes?.length ?? 0) > 0;

  const body = (
    <>
      {summary.summary && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: 'var(--text-2)', lineHeight: 1.7 }}>
          {summary.summary}
        </p>
      )}
      {(hasChars || hasScenes) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {hasChars && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-3)', flexShrink: 0 }}>{t('episodeSummary.keyCharacters')}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {summary.key_characters.map(name => <span key={name} style={tagStyle}>{name}</span>)}
              </div>
            </div>
          )}
          {hasScenes && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-3)', flexShrink: 0 }}>{t('episodeSummary.keyScenes')}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {summary.key_scenes.map(name => <span key={name} style={tagStyle}>{name}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {/* 始终在文档流中：视野内完整展开，无收起按钮、不可折叠 */}
      <div
        ref={cardRef}
        className="card theme-left-border"
        style={{ borderRadius: '10px', '--_theme-color': 'var(--theme-storyboard)', '--_theme-color-end': '#3b82f6' } as React.CSSProperties}
      >
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--theme-storyboard)' }}>
          {t('episodeSummary.title')}
        </h3>
        {body}
      </div>

      {/* 滚出视野后：右上角胶囊 */}
      {scrolledPast && (
        <button
          onClick={() => setPanelOpen(v => !v)}
          style={{
            position: 'fixed', top: 'calc(var(--header-height) + 8px)', right: '24px', zIndex: 82,
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'linear-gradient(135deg, var(--theme-storyboard), #3b82f6)', color: '#fff',
            border: 'none', borderRadius: '999px', cursor: 'pointer',
            padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, boxShadow: 'var(--shadow-sm)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          {t('episodeSummary.title')}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points={panelOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
          </svg>
        </button>
      )}

      {/* 胶囊点开：完全吸顶的悬浮面板 */}
      {scrolledPast && panelOpen && (
        <>
          {/* 剧本模式不放遮罩：让用户一边看剧本一边操作页面；梗概模式保留点外部即收起 */}
          {mode === 'summary' && (
            <div onClick={() => setPanelOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 80 }} />
          )}
          <div
            className="theme-left-border"
            style={{
              position: 'fixed', top: 'var(--header-height)', right: '16px', zIndex: 81,
              width: mode === 'script' ? '560px' : '480px', maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - var(--header-height) - 16px)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              background: 'var(--surface)', borderRadius: '0 0 12px 12px', padding: '1.25rem',
              boxShadow: 'var(--shadow-lg)',
              '--_theme-color': 'var(--theme-storyboard)', '--_theme-color-end': '#3b82f6',
            } as React.CSSProperties}
          >
            {/* 按钮紧跟标题靠左排——右上角被悬浮胶囊（zIndex 更高）盖住，放右侧会被挡住点不到 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 0 0.6rem', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--theme-storyboard)' }}>
                {mode === 'script' ? t('content.title') : t('episodeSummary.title')}
              </h3>
              {scriptView && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setMode(m => m === 'script' ? 'summary' : 'script')}
                  style={{ flexShrink: 0 }}
                >
                  {mode === 'script' ? t('episodeSummary.backToSummary') : t('episodeSummary.viewScript')}
                </button>
              )}
            </div>
            {/* 只滚内容：标题行/框体固定。剧本模式交给 scriptView 内部滚（tab 药丸也固定），梗概模式整块滚 */}
            <div
              ref={summaryWrapRef}
              onScroll={(e) => { if (mode === 'summary') summaryScrollPos.current = e.currentTarget.scrollTop; }}
              style={{ flex: 1, minHeight: 0, ...(mode === 'script'
                ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' }
                : { overflowY: 'auto' }) } as React.CSSProperties}
            >
              {mode === 'script' ? scriptView : body}
            </div>
          </div>
        </>
      )}
    </>
  );
}
